import React, { useMemo, useEffect } from "react";
import { renderReact } from "../src/lib/ssrHydrator";
import ArticleSearchContainer from "../src/ArticleSearchForm/containers/ArticleSearchContainer";
import InsertContentRenderer from "../src/ArticleSearchForm/components/common/InsertContentRenderer";
import AdRenderer from "../src/components/AdRenderer";
import PCArticleBox from "../src/components/ArticleBox/pc/index";
import PrArticleBox from "../src/components/PrArticleBox";
import PCRecommendArticlesBox from "../src/components/RecommendArticlesBox/pc/index";
import RelatedAreaPathsBox from "../src/components/RelatedPaths/pc/index";
import Article from "../src/entities/article";
import { setCustomEvent } from "../src/utils/analize";

const createRecPartParam = (recommendPartTime) => ({
  heading: "似た条件のアルバイト・パート",
  articles: recommendPartTime,
});

const createRecAnywhereParam = (recommendOnline) => ({
  heading: "似た条件のオンラインボランティア",
  articles: recommendOnline,
});

const createRelatedAreaPathParam = (relatedAreaPath) =>
  Object.keys(relatedAreaPath).length > 0
    ? { heading: "エリアを変更する", params: relatedAreaPath }
    : {};

const createInsertContents = ({
  results = [],
  recommendPartTime = [],
  recommendOnline = [],
  prContents = [],
  relatedAreaPath = [],
  volOrJob = [],
  ads = {},
}) => {
  const count = results.length;
  const recPart = createRecPartParam(recommendPartTime);
  const recAnywhere = createRecAnywhereParam(recommendOnline);
  const relatedArea = createRelatedAreaPathParam(relatedAreaPath);
  const setting = {};

  const ensureEven = (index, count) => {
    if (index % 2 !== 0) {
      index += 1;
      if (index >= count) index -= 2;
    }
    return index;
  };

  if (volOrJob == "volunteer") {
    setting[0] = (
      <>
        {prContents.length > 0 && (
          <PrArticleBox article={prContents[0]} key="PrArticleBox-0" />
        )}
        <div id="jsFirstAnnounce" />
      </>
    );
    if (count > 2 && ads.activo_pc_search_main_top) {
      setting[2] = (
        <AssignOrPush
          target={setting[2]}
          content={
            <AdRenderer
              html={ads.activo_pc_search_main_top}
              key="AdRenderer-pc-main-top"
            />
          }
        />
      );
    }
    if (count > 9) {
      const prContentIndex = ensureEven(Math.round((count * 2) / 4), count);
      setting[prContentIndex] = (
        prContents.length > 1 && (
          <PrArticleBox
            article={prContents[1]}
            key={`PrArticleBox-${prContentIndex}`}
          />
        )
      );
    }
    if (
      count > 4 &&
      recommendPartTime.length > 0 &&
      recommendOnline.length > 0
    ) {
      setting[2] = (
        <AssignOrPush
          target={setting[2]}
          content={
            <PCRecommendArticlesBox
              {...recPart}
              idx={2}
              type="PartTime"
              key="PCRecommendArticlesBox-2"
            />
          }
        />
      );
      const recommendOnlineIndex = ensureEven(count - 2, count);
      setting[recommendOnlineIndex] = (
        <AssignOrPush
          target={setting[recommendOnlineIndex]}
          content={
            <PCRecommendArticlesBox
              {...recAnywhere}
              idx={recommendOnlineIndex}
              type="Online"
              key={`PCRecommendArticlesBox-${recommendOnlineIndex}`}
            />
          }
        />
      );
    } else if (count > 2 && recommendPartTime.length > 0) {
      setting[2] = (
        <AssignOrPush
          target={setting[2]}
          content={
            <PCRecommendArticlesBox
              {...recPart}
              idx={2}
              type="PartTime"
              key="PCRecommendArticlesBox-2"
            />
          }
        />
      );
    } else if (count > 2 && recommendOnline.length > 0) {
      const recommendOnlineIndex = ensureEven(count - 2, count);
      setting[recommendOnlineIndex] = (
        <AssignOrPush
          target={setting[recommendOnlineIndex]}
          content={
            <PCRecommendArticlesBox
              {...recAnywhere}
              idx={recommendOnlineIndex}
              type="Online"
              key={`PCRecommendArticlesBox-${recommendOnlineIndex}`}
            />
          }
        />
      );
    }
  }
  if (Object.keys(relatedArea).length && count > 6) {
    setting[6] = (
      <AssignOrPush
        target={setting[6]}
        content={
          <RelatedAreaPathsBox
            {...relatedArea}
            idx={6}
            key="RelatedAreaPathsBox-6"
          />
        }
      />
    );
  }
  return setting;
};

function NotFoundMessage() {
  useEffect(() => {
    setCustomEvent("noresult");
  }, []);
  return (
    <h2 className="resultHeaderDesc">
      条件に当てはまる募集は見つかりませんでした。
    </h2>
  );
}

function AssignOrPush(props) {
  return <>{props.target ? [props.target, props.content] : props.content}</>;
}

export function PCArticleListWrapper() {
  const articleSearch = ArticleSearchContainer.useContainer();
  const args = {
    results: articleSearch.results,
    recommendPartTime: articleSearch.recommendPartTime,
    recommendOnline: articleSearch.recommendOnline,
    prContents: articleSearch.prContents,
    relatedAreaPath: articleSearch.relatedAreaPath,
    volOrJob: articleSearch.volOrJob,
    ads: articleSearch.ads,
  };
  const insertContents = useMemo(
    () => createInsertContents(args),
    [
      articleSearch.results,
      articleSearch.recommendPartTime,
      articleSearch.recommendOnline,
      articleSearch.relatedAreaPath,
    ]
  );
  return (
    <PCArticleList
      results={articleSearch.results}
      insert_contents={insertContents}
      html_id={articleSearch.wrapperHtmlId}
      html_data={articleSearch.wrapperHtmlData}
      page={articleSearch.page}
    />
  );
}

const PCArticleList = React.memo(
  ({
    results,
    insert_contents = {},
    border_none = false,
    html_id = "",
    html_data = {},
    page = 1,
  }) => {
    const parsed_html_data = {};

    Object.keys(html_data).forEach((key) => {
      parsed_html_data[`data-${key}`] = html_data[key];
    });

    return results.length == 0 ? (
      <NotFoundMessage />
    ) : (
      <div
        className={`m_mainArticleList has${border_none ? "Not" : ""}Border`}
        id={html_id}
        {...parsed_html_data}
      >
        {results.map((result, i) => (
          <React.Fragment key={result.id}>
            <InsertContentRenderer insertContent={insert_contents[i]} />
            <PCArticleBox article={new Article({ ...result })} />
          </React.Fragment>
        ))}
        <InsertContentRenderer
          insertContent={insert_contents[results.length]}
        />
      </div>
    );
  }
);

export const _PCArticleList = renderReact("PCArticleList", PCArticleList);
